import { DataObjectOutlined, DownloadOutlined, ImageOutlined } from '@mui/icons-material';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { FormattedDateField } from 'components/FormattedDateField';
import { FC, useEffect, useState } from 'react';
import {
  Button,
  Datagrid,
  FunctionField,
  ListContextProvider,
  ListControllerResult,
  ListProps,
  RaRecord,
  TextField,
  WithRecord,
  useDataProvider,
  useListController,
  useNotify,
  useRecordContext,
  useTranslate,
} from 'react-admin';

interface DiagnosticAttachment {
  id: number;
  file_name: string;
  mime_type: string;
  size: number;
  created_at: string;
  download_url: string;
}

interface DiagnosticDialogProps {
  open: boolean;
  onClose: () => void;
  record?: RaRecord;
}

const isImageMimeType = (mimeType: string): boolean => mimeType.startsWith('image/');

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const queryClient = new QueryClient();

const DiagnosticDialog = ({ open, onClose, record }: DiagnosticDialogProps) => {
  if (!record) return;

  const report = typeof record.data === 'object' ? record.data : JSON.parse(record.data);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{report?.script ?? record.id}</DialogTitle>
      <DialogContent>
        <pre>
          <code>{JSON.stringify(report, null, 2)}</code>
        </pre>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button label="ra.action.cancel" onClick={onClose} variant="text" />
        <Button
          label="custom.actions.copy"
          variant="contained"
          onClick={() => navigator.clipboard.writeText(JSON.stringify(report))}
        />
      </DialogActions>
    </Dialog>
  );
};

interface ImagePreviewDialogProps {
  open: boolean;
  onClose: () => void;
  filename: string;
  blobUrl: string | null;
}

const ImagePreviewDialog: FC<ImagePreviewDialogProps> = ({ open, onClose, filename, blobUrl }) => (
  <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
    <DialogTitle>{filename}</DialogTitle>
    <DialogContent sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
      {blobUrl ? (
        <img src={blobUrl} alt={filename} style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
      ) : (
        <Box sx={{ py: 4, color: 'text.secondary' }}>Loading…</Box>
      )}
    </DialogContent>
    <DialogActions sx={{ px: 3, py: 2 }}>
      <Button label="ra.action.cancel" onClick={onClose} variant="text" />
    </DialogActions>
  </Dialog>
);

interface DiagnosticAttachmentsProps {
  record: RaRecord;
}

const DiagnosticAttachments: FC<DiagnosticAttachmentsProps> = ({ record }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const translate = useTranslate();

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFilename, setPreviewFilename] = useState('');
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
    };
  }, [previewBlobUrl]);

  const attachments: DiagnosticAttachment[] = record.attachments ?? [];

  if (attachments.length === 0) return <Box />;

  const handleDownload = async (attachment: DiagnosticAttachment) => {
    try {
      notify('custom.notification.download_started', {
        type: 'info',
        messageArgs: { item: attachment.file_name },
      });
      await dataProvider.downloadByPath(attachment.download_url, attachment.file_name);
      notify('custom.notification.download', {
        type: 'success',
        messageArgs: { item: attachment.file_name },
      });
    } catch {
      notify('ra.notification.http_error', { type: 'error' });
    }
  };

  const handlePreview = async (attachment: DiagnosticAttachment) => {
    setPreviewFilename(attachment.file_name);
    setPreviewBlobUrl(null);
    setPreviewOpen(true);

    try {
      const blob: Blob = await dataProvider.fetchBlob(attachment.download_url);
      setPreviewBlobUrl(URL.createObjectURL(blob));
    } catch {
      notify('ra.notification.http_error', { type: 'error' });
      setPreviewOpen(false);
    }
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
    if (previewBlobUrl) {
      URL.revokeObjectURL(previewBlobUrl);
      setPreviewBlobUrl(null);
    }
  };

  return (
    <>
      <Stack direction="column" spacing={0.5}>
        {attachments.map((attachment) => (
          <Stack key={attachment.id} direction="row" spacing={0.5} alignItems="center">
            <Button
              label={attachment.file_name}
              size="small"
              startIcon={<DownloadOutlined />}
              onClick={() => handleDownload(attachment)}
            />
            {isImageMimeType(attachment.mime_type) && (
              <Button
                label={translate('custom.actions.preview')}
                size="small"
                startIcon={<ImageOutlined />}
                onClick={() => handlePreview(attachment)}
                title={attachment.file_name}
              />
            )}
            <Typography variant="caption" color="text.secondary">
              {formatBytes(attachment.size)}
            </Typography>
          </Stack>
        ))}
      </Stack>
      <ImagePreviewDialog
        open={previewOpen}
        onClose={handlePreviewClose}
        filename={previewFilename}
        blobUrl={previewBlobUrl}
      />
    </>
  );
};

export const DiagnosticsList: FC<Omit<ListProps, 'children'>> = () => {
  const translate = useTranslate();
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const {
    data: reports,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['diagnostics'],
    queryFn: () => dataProvider.getDiagnostics('units', { serial: record?.serial }),
  });

  const { data: list, total, isLoading: load, error: listError, ...controller } = useListController();
  const [open, setOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<RaRecord>();

  if (!reports) return null;

  const openDialog = (record: RaRecord) => {
    setSelectedRecord(record);
    setOpen(true);
  };
  const closeDialog = () => setOpen(false);

  return (
    <QueryClientProvider client={queryClient}>
      <ListContextProvider
        value={
          {
            data: reports.data,
            total: reports.total,
            isLoading,
            error: error instanceof Error ? error : null,
            ...controller,
          } as ListControllerResult
        }
      >
        <Datagrid resource="diagnostics" bulkActionButtons={false}>
          <WithRecord
            label="resources.diagnostics.fields.identifier"
            render={(record) => <TextField source="data.script" emptyText={record.id.toString()} />}
          />
          <FormattedDateField source="created_at" />
          <TextField source="type" />
          <FunctionField
            label="resources.diagnostics.fields.attachments"
            render={(diagnosticRecord: RaRecord) => <DiagnosticAttachments record={diagnosticRecord} />}
          />
          <FunctionField
            render={(record: RaRecord) => (
              <Button
                onClick={() => openDialog(record)}
                label={translate('custom.actions.show', { name: 'JSON' })}
                size="small"
                startIcon={<DataObjectOutlined />}
              />
            )}
          />
        </Datagrid>
        <DiagnosticDialog open={open} onClose={closeDialog} record={selectedRecord} />
      </ListContextProvider>
    </QueryClientProvider>
  );
};
